import { Box, IconButton, Popper, Stack, alpha } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Instance } from '@popperjs/core';
import styles from '../FormRichText.styles';
import Quill from 'quill';
import IconMi from 'components/common/icon/IconMi';
interface RichTextImageTooltipProps {
    enableImageAlign?: boolean;
    quillEditorRef: any;
    quill: any;
    clickOutside: number;
    updateImageHandler: number;
    setIsActiveImage: (value: boolean) => void;
}
export default function RichTextImageTooltip(props: RichTextImageTooltipProps) {
    const {
        quillEditorRef,
        quill,
        enableImageAlign = true,
        clickOutside,
        updateImageHandler,
        setIsActiveImage = () => {},
    } = props;
    const [activeState, setActiveState] = useState<boolean>(false);

    const activeImageEl = useRef<any>(null);
    const imageHandler = useRef<any>(null);
    const isResizeActive = useRef<boolean>(false);
    const isResizeBySide = useRef<string>('right');
    const resizeInitialPos = useRef<{ left: number; right: number }>({ left: 0, right: 0 });
    const imageTooltipPopperRef = useRef<Instance>(null);
    const activeImageIndex = useRef<number>(0);
    const activeImageAlignment = useRef<string>('');

    const unselectActiveImage = () => {
        setActiveState(false);
        setIsActiveImage(false);
        activeImageEl.current = null;
        isResizeActive.current = false;
        if (quillEditorRef.current) {
            quillEditorRef.current.querySelectorAll('.image-active').forEach((el: any) => {
                el.classList.remove('image-active');
            });
        }
        if (imageHandler.current) {
            imageHandler.current.classList.remove('active');
        }
    };

    const updateImageHandlerPosition = () => {
        if (activeImageEl.current && quillEditorRef.current && imageHandler.current) {
            const imageRect = activeImageEl.current.getBoundingClientRect();
            const containerRect = quillEditorRef.current.getBoundingClientRect();

            imageHandler.current.style.top = imageRect.top - containerRect.top - 1 + 'px';
            imageHandler.current.style.left = imageRect.left - containerRect.left - 1 + 'px';
            imageHandler.current.style.width = imageRect.width + 2 + 'px';
            imageHandler.current.style.height = imageRect.height + 2 + 'px';

            if (imageTooltipPopperRef.current != null) {
                imageTooltipPopperRef.current.update();
            }
        }
    };
    const onImageClick = (event: any) => {
        unselectActiveImage();
        if (event.target.tagName.toLowerCase() === 'img') {
            const imageBlot = Quill.find(event.target);
            activeImageIndex.current = quill.getIndex(imageBlot);
            activeImageAlignment.current = quill.getFormat(activeImageIndex.current)?.align ?? '';
            activeImageEl.current = event.target;

            setActiveState(true);
            setIsActiveImage(true);
            event.target.classList.add('image-active');

            imageHandler.current.classList.add('active');
            updateImageHandlerPosition();
            quill.blur();
        }
    };

    const onMouseMove = (event: any) => {
        if (isResizeActive.current && activeImageEl.current) {
            const imageRect = activeImageEl.current.getBoundingClientRect();
            let newWidth = 0;
            if (isResizeBySide.current === 'left') {
                //newWidth = imageRect.right - event.clientX;
                newWidth = resizeInitialPos.current.right - event.clientX;
            } else {
                //newWidth = event.clientX - imageRect.left;
                newWidth = event.clientX - resizeInitialPos.current.left;
            }
            if (newWidth > 14) {
                activeImageEl.current.width = newWidth;
            }

            updateImageHandlerPosition();
        }
    };
    const onMouseDown = (event: any) => {
        isResizeBySide.current = event.target.classList.contains('right') ? 'right' : 'left';
        isResizeActive.current = true;
        imageHandler.current.classList.add('resizing');
        const imageRect = activeImageEl.current.getBoundingClientRect();
        resizeInitialPos.current = { left: imageRect.left, right: imageRect.right };
        event.preventDefault();

        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);
    };
    const onMouseUp = (event: any) => {
        isResizeActive.current = false;
        imageHandler.current.classList.remove('resizing');
        document.removeEventListener('mouseup', onMouseUp);
        document.removeEventListener('mousemove', onMouseMove);
    };

    const addImageHandler = () => {
        const handle = document.createElement('div');
        handle.classList.add('image-active-handle');

        const leftResizeHandle = document.createElement('div');
        leftResizeHandle.classList.add('resize-handle', 'left');
        handle.appendChild(leftResizeHandle);

        const rightResizeHandle = document.createElement('div');
        rightResizeHandle.classList.add('resize-handle', 'right');
        handle.appendChild(rightResizeHandle);

        quillEditorRef.current.appendChild(handle);

        handle.querySelectorAll('.resize-handle').forEach((resizeHandler) => {
            resizeHandler.addEventListener('mousedown', onMouseDown);
        });
        imageHandler.current = handle;
    };

    useEffect(() => {
        document.addEventListener('keydown', keyPressHandler);
        if (quillEditorRef.current) {
            const editorEl = quillEditorRef.current.querySelector('.ql-editor');
            if (editorEl) {
                editorEl.addEventListener('click', onImageClick);
            }
            addImageHandler();
        }

        return () => {
            document.removeEventListener('keydown', keyPressHandler);
            if (quillEditorRef.current) {
                const editorEl = quillEditorRef.current.querySelector('.ql-editor');
                if (editorEl) {
                    editorEl.removeEventListener('click', onImageClick);
                }
            }
            if (imageHandler.current) {
                imageHandler.current.querySelectorAll('.resize-handle').forEach((resizeHandler: any) => {
                    resizeHandler.removeEventListener('mousedown', onMouseDown);
                });
            }
        };
    }, []);

    const setImageAlign = (align: string) => {
        quill.setSelection(activeImageIndex.current, 0, 'silent');
        quill.format('align', align);
        unselectActiveImage();
    };

    const downloadImage = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        const link = document.createElement('a');
        let [leaf, offset] = quill.getLeaf(activeImageIndex.current + 1);
        const currentSrc = leaf.domNode.src;

        const mimeMatch = currentSrc.match(/^data:(image\/[a-zA-Z]+);base64,/);
        let extension = '';
        if (mimeMatch) {
            const mimeType = mimeMatch[1];
            const mimeToExt: any = {
                'image/jpeg': 'jpg',
                'image/png': 'png',
            };
            extension = mimeToExt[mimeType] || '';
        }
        let fileName = currentSrc.split(',')[1].slice(-10).replaceAll('+', '-').replaceAll('/', '_');

        link.href = currentSrc;
        link.download = fileName + (extension ? `.${extension}` : '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        unselectActiveImage();
    };
    const removeImage = () => {
        quill.deleteText(activeImageIndex.current, 1);
        quill.setSelection(activeImageIndex.current);
        unselectActiveImage();
    };

    useEffect(() => {
        if (activeImageEl.current) {
            unselectActiveImage();
        }
    }, [clickOutside]);

    useEffect(() => {
        if (activeImageEl.current) {
            updateImageHandlerPosition();
        }
    }, [updateImageHandler]);

    const keyPressHandler = useCallback((event: KeyboardEvent) => {
        switch (event.key) {
            case 'Backspace':
            case 'Delete':
                if (activeImageEl.current) {
                    removeImage();
                    event.preventDefault();
                }
                break;
        }
    }, []);
    useEffect(() => {
        if (activeState) {
            document.addEventListener('keydown', keyPressHandler);
        } else {
            document.removeEventListener('keydown', keyPressHandler);
        }
    }, [activeState]);
    return (
        <Popper
            placement={'top-start'}
            open={activeState}
            popperRef={imageTooltipPopperRef}
            disablePortal={true}
            anchorEl={imageHandler.current}
        >
            <Box
                sx={styles.imageTooltipContainer}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }}
            >
                <Stack sx={styles.imageTooltipContent} direction={'row'} alignItems={'center'} spacing={0.5}>
                    {enableImageAlign && (
                        <>
                            {['', 'center', 'right'].map((align) => {
                                return (
                                    <IconButton
                                        key={align}
                                        sx={styles.imageTooltipButton}
                                        className={activeImageAlignment.current == align ? 'active' : ''}
                                        onClick={() => {
                                            setImageAlign(align);
                                        }}
                                    >
                                        <Box sx={{ display: 'inline-flex' }}>
                                            <IconMi
                                                icon={`alignment-${align > '' ? align : 'left'}`}
                                                sx={{ fontSize: '16px' }}
                                            />
                                        </Box>
                                    </IconButton>
                                );
                            })}
                            <Box sx={styles.imageTooltipDivider} />
                        </>
                    )}

                    <IconButton sx={styles.imageTooltipButton} onClick={downloadImage}>
                        <Box sx={{ display: 'inline-flex' }}>
                            <IconMi icon={'download'} sx={{ fontSize: '16px' }} />
                        </Box>
                    </IconButton>
                    <Box sx={styles.imageTooltipDivider} />
                    <IconButton sx={styles.imageTooltipButton} onClick={removeImage}>
                        <Box sx={{ display: 'inline-flex' }}>
                            <IconMi icon={'delete'} sx={{ fontSize: '16px' }} />
                        </Box>
                    </IconButton>
                </Stack>
            </Box>
        </Popper>
    );
}
